import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { validationThrowSchema } from '../schema/errorDemoSchema';
import type { ValidationThrowFormValues } from '../schema/errorDemoSchema';
import { useValidationThrow } from '../hooks/useThrowDemo';
import { ThrowResultCard } from './ThrowResultCard';

/**
 * Panel for POST /api/eraf/error-demo/simulate/validation (R8-E-001: was /throw/validation).
 * Demonstrates @Email / @Phone Bean Validation → GlobalExceptionHandler 400.
 */
export function ValidationThrowPanel() {
  const { result, throwValidation } = useValidationThrow();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ValidationThrowFormValues>({
    resolver: zodResolver(validationThrowSchema),
    defaultValues: {
      email: 'not-an-email',
      phone: '01012345',
    },
  });

  const onSubmit = async (values: ValidationThrowFormValues) => {
    await throwValidation({
      email: values.email,
      phone: values.phone,
    });
  };

  return (
    <div
      style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-4)' }}
      aria-label="Validation throw 패널"
    >
      {/* Panel header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
        <span
          style={{
            fontSize: 'var(--font-size-base)',
            fontWeight: 700,
            color: 'var(--color-text-primary)',
          }}
        >
          Validation throw
        </span>
        <span
          style={{
            fontSize: 'var(--font-size-xs)',
            padding: '2px var(--spacing-2)',
            borderRadius: 'var(--radius-full)',
            background: 'var(--color-surface-elevated)',
            border: '1px solid var(--color-surface-border)',
            color: 'var(--color-text-muted)',
            fontFamily: 'var(--font-family-mono)',
          }}
        >
          POST /simulate/validation
        </span>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit(onSubmit)} noValidate aria-label="Validation throw 폼">
        {/* Email field */}
        <div style={{ marginBottom: 'var(--spacing-4)' }}>
          <label
            htmlFor="validation-email"
            style={{
              display: 'block',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              color: 'var(--color-text-secondary)',
              marginBottom: 'var(--spacing-1)',
            }}
          >
            이메일 (의도적으로 잘못된 값 입력)
          </label>
          <input
            id="validation-email"
            type="email"
            {...register('email')}
            placeholder="예: not-an-email"
            aria-describedby={errors.email ? 'validation-email-error' : undefined}
            aria-invalid={!!errors.email}
            style={{
              width: '100%',
              padding: 'var(--spacing-2) var(--spacing-3)',
              border: `1px solid ${errors.email ? 'var(--color-danger)' : 'var(--color-surface-border)'}`,
              borderRadius: 'var(--radius-sm)',
              fontSize: 'var(--font-size-sm)',
              background: 'var(--color-surface-background)',
              color: 'var(--color-text-primary)',
              fontFamily: 'var(--font-family-mono)',
              outline: 'none',
            }}
          />
          {errors.email && (
            <span
              id="validation-email-error"
              role="alert"
              style={{
                display: 'block',
                fontSize: 'var(--font-size-xs)',
                color: 'var(--color-danger)',
                marginTop: 'var(--spacing-1)',
              }}
            >
              {errors.email.message}
            </span>
          )}
        </div>

        {/* Phone field */}
        <div style={{ marginBottom: 'var(--spacing-4)' }}>
          <label
            htmlFor="validation-phone"
            style={{
              display: 'block',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              color: 'var(--color-text-secondary)',
              marginBottom: 'var(--spacing-1)',
            }}
          >
            전화번호 (옵션)
          </label>
          <input
            id="validation-phone"
            type="tel"
            {...register('phone')}
            placeholder="예: 01012345"
            style={{
              width: '100%',
              padding: 'var(--spacing-2) var(--spacing-3)',
              border: '1px solid var(--color-surface-border)',
              borderRadius: 'var(--radius-sm)',
              fontSize: 'var(--font-size-sm)',
              background: 'var(--color-surface-background)',
              color: 'var(--color-text-primary)',
              fontFamily: 'var(--font-family-mono)',
              outline: 'none',
            }}
          />
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={isSubmitting || result.status === 'loading'}
          aria-busy={isSubmitting || result.status === 'loading'}
          style={{
            padding: 'var(--spacing-2) var(--spacing-6)',
            background:
              isSubmitting || result.status === 'loading'
                ? 'var(--color-surface-elevated)'
                : 'var(--color-brand-primary)',
            color:
              isSubmitting || result.status === 'loading'
                ? 'var(--color-text-muted)'
                : 'var(--color-text-inverse)',
            border: 'none',
            borderRadius: 'var(--radius-sm)',
            fontSize: 'var(--font-size-sm)',
            fontWeight: 700,
            cursor: isSubmitting || result.status === 'loading' ? 'not-allowed' : 'pointer',
            fontFamily: 'var(--font-family-sans)',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 'var(--spacing-2)',
          }}
        >
          {(isSubmitting || result.status === 'loading') && (
            <span
              aria-hidden="true"
              style={{
                display: 'inline-block',
                width: 14,
                height: 14,
                border: '2px solid currentColor',
                borderTopColor: 'transparent',
                borderRadius: 'var(--radius-full)',
                animation: 'spin 0.75s linear infinite',
              }}
            />
          )}
          throw
        </button>
      </form>

      {/* Hint */}
      <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', fontStyle: 'italic' }}>
        @Valid + @Email / @Phone 위반 → GlobalExceptionHandler 자동 처리.
      </p>

      {/* Result */}
      <ThrowResultCard result={result} />
    </div>
  );
}
